import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";

import { db, isFirebaseConfigured } from "../lib/firebase";
import { unlockedWords } from "../lib/tokiPonaData";
import { todaysWord } from "../lib/tokiPonaGarden";
import BookView from "../components/konNimi/BookView";
import GardenMap from "../components/konNimi/GardenMap";
import WordPage from "../components/konNimi/WordPage";
import "../styles/konnimi.css";

/* ============================================================
   kon nimi — a toki pona word-journal garden.
   Save state is per-user: shares the trainer's anonymous device id,
   and mirrors the whole journal to Firestore at
   tokipona-journal/{userId} (falling back to localStorage when
   Firebase isn't configured).
   ============================================================ */

const USER_KEY = "tokiPonaUserId"; // shared with the trainer — same anonymous id
const LOCAL_JOURNAL_KEY = "konNimiJournal"; // offline mirror

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getUserId = () => {
  let id = localStorage.getItem(USER_KEY);
  if (!id) {
    id = newId();
    localStorage.setItem(USER_KEY, id);
  }
  return id;
};

const KonNimi = () => {
  const [word, setWord] = useState(null);
  const [view, setView] = useState("garden"); // "garden" | "book" — word takes precedence over this
  const [journal, setJournal] = useState({ entries: {}, links: [] });
  const [loaded, setLoaded] = useState(false);

  const docRef = useMemo(
    () => (isFirebaseConfigured && db ? doc(db, "tokipona-journal", getUserId()) : null),
    []
  );

  useEffect(() => {
    if (!docRef) {
      try {
        const saved = localStorage.getItem(LOCAL_JOURNAL_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          setJournal({ entries: parsed.entries || {}, links: parsed.links || [] });
        }
      } catch {
        /* ignore */
      }
      setLoaded(true);
      return undefined;
    }
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        setJournal({
          entries: (snap.exists() ? snap.data().entries : {}) || {},
          links: (snap.exists() ? snap.data().links : []) || [],
        });
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return unsub;
  }, [docRef]);

  const saveEntry = (forWord, promptId, text) => {
    const entry = { text, at: Date.now() };
    const nextEntries = {
      ...journal.entries,
      [forWord]: { ...(journal.entries[forWord] || {}), [promptId]: entry },
    };
    const next = { entries: nextEntries, links: journal.links };
    setJournal(next);
    try {
      localStorage.setItem(LOCAL_JOURNAL_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private-mode */
    }
    if (docRef) {
      setDoc(
        docRef,
        { entries: { [forWord]: { [promptId]: entry } }, updatedAt: Date.now() },
        { merge: true }
      ).catch(() => {});
    }
  };

  const addLink = (from, to, note) => {
    const target = to.trim().toLowerCase();
    const link = { from, to: target, note: note.trim(), at: Date.now() };
    const nextLinks = [...journal.links, link];
    const next = { entries: journal.entries, links: nextLinks };
    setJournal(next);
    try {
      localStorage.setItem(LOCAL_JOURNAL_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private-mode */
    }
    if (docRef) {
      // merge replaces arrays wholesale, which is exactly what we want here
      setDoc(
        docRef,
        { links: nextLinks, updatedAt: Date.now() },
        { merge: true }
      ).catch(() => {});
    }
  };

  // Local-only read of the trainer's progress, just for a subtle "learned"
  // glow on words already met in lessons — good enough for that, no need
  // to sync it.
  const learnedSet = useMemo(() => {
    try {
      const completed = JSON.parse(localStorage.getItem("tokiPonaProgress"))?.completed;
      return new Set(unlockedWords(completed || {}));
    } catch {
      return new Set();
    }
  }, []);

  // A gentle daily nudge toward one of the least-grown plants — the same
  // word for everyone all day, a different one tomorrow.
  const todayWord = useMemo(() => {
    const dateKey = new Date().toISOString().slice(0, 10);
    return todaysWord(dateKey, journal.entries);
  }, [journal.entries]);

  const openWord = (w) => {
    setWord(w);
    window.scrollTo(0, 0);
  };

  const backToGarden = () => {
    setWord(null);
    setView("garden");
    window.scrollTo(0, 0);
  };

  const toggleBook = () => {
    setWord(null);
    setView((v) => (v === "book" ? "garden" : "book"));
    window.scrollTo(0, 0);
  };

  return (
    <div className="kn-root">
      <div className="kn-topbar">
        <Link to="/o-kama-sona" className="kn-back">
          ‹ o kama sona
        </Link>
        <span className="kn-title">kon nimi</span>
        <button
          type="button"
          className="kn-topbar-book"
          aria-pressed={view === "book"}
          onClick={toggleBook}
        >
          {view === "book" ? "the garden" : "lipu mi"}
        </button>
      </div>
      {!loaded ? null : word ? (
        <WordPage
          word={word}
          wordEntries={journal.entries[word] || {}}
          learned={learnedSet.has(word)}
          onSave={(pid, text) => saveEntry(word, pid, text)}
          onBack={backToGarden}
          links={journal.links}
          onAddLink={(to, note) => addLink(word, to, note)}
        />
      ) : view === "book" ? (
        <BookView entries={journal.entries} links={journal.links} onOpenWord={openWord} />
      ) : (
        <GardenMap
          entries={journal.entries}
          learned={learnedSet}
          links={journal.links}
          todayWord={todayWord}
          onOpenWord={openWord}
        />
      )}
    </div>
  );
};

export default KonNimi;
